import { useState } from "react";

export const formState = {
  pristine: "pristine",
  touched: "touched",
  error: "error",
} as const;

export type FormState = (typeof formState)[keyof typeof formState];

/* I have created a custom hook and haven't used a library because the fluid design system inputs override onChange event
 * to return a value. The library relies on the native onChangeEvent to work.
 */
export function useForm<
  T extends Record<string, string | string[] | number | boolean | undefined>,
>({
  handleSubmit,
  requiredFields,
}: {
  handleSubmit: (data: T) => void;
  requiredFields: Record<string, string>;
}) {
  const [formStatus, setFormStatus] = useState<FormState>(formState.pristine);
  const [errors, setErrors] = useState<string[]>([]);
  const onSubmit = async (data: T) => {
    const invalidFields = Object.keys(requiredFields).filter(
      (fieldName) =>
        !(fieldName in data) ||
        !data[fieldName] ||
        (Array.isArray(data[fieldName]) && !data[fieldName].length),
    );

    if (invalidFields.length > 0) {
      setErrors(invalidFields.map((field) => requiredFields[field]));
      setFormStatus("error");
      return;
    }

    setFormStatus(formState.touched);
    handleSubmit(data);
  };

  return {
    formStatus,
    errors,
    onSubmit,
  };
}
